import { Mic, Square } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Spinner } from '../../components/Spinner';
import { pttArmado, pttGrabando, TOPE_GRABACION_MS, type EstadoSesion } from './sesionMachine';

/** Leyenda bajo el botón según qué está pasando en el turno. */
export function leyendaPtt(s: EstadoSesion): string {
  if (pttGrabando(s)) return 'Grabando. Hacer clic para terminar y enviar.';
  if (pttArmado(s)) return 'Hacer clic para hablar.';
  if (s.reproduciendo) return 'El paciente está hablando…';
  if (s.turno.etapa === 'procesando') return 'Enviando…';
  if (s.turno.etapa === 'esperando') return 'El paciente está pensando…';
  return '';
}

/**
 * Botón de micrófono, clic para empezar y clic para terminar (D-6;
 * especificación §4.9): grande, evidente, con el estado del turno a la
 * vista. Muestra cuánto se lleva grabado contra el tope de 60 s.
 */
export function BotonPtt({ sesion, onPulsar }: { sesion: EstadoSesion; onPulsar: () => void }) {
  const grabando = pttGrabando(sesion);
  const habilitado = grabando || pttArmado(sesion);
  const segundos = useSegundosGrabando(grabando);
  const ocupado = !habilitado && sesion.turno.etapa !== 'libre';

  return (
    <div className="flex flex-col items-center gap-2">
      <button
        type="button"
        onClick={onPulsar}
        disabled={!habilitado}
        aria-pressed={grabando}
        aria-label={grabando ? 'Terminar de hablar' : 'Hablar'}
        className={`flex h-20 w-20 items-center justify-center rounded-full text-white shadow-lg transition focus-visible:outline-none focus-visible:ring-4 disabled:cursor-not-allowed disabled:bg-slate-400 disabled:shadow-none sm:h-24 sm:w-24 ${
          grabando
            ? 'animate-pulse bg-red-600 focus-visible:ring-red-300'
            : 'bg-primary-600 hover:bg-primary-700 focus-visible:ring-primary-300'
        }`}
      >
        {ocupado ? (
          <Spinner size={28} />
        ) : grabando ? (
          <Square size={30} fill="currentColor" />
        ) : (
          <Mic size={36} />
        )}
      </button>
      <p className="min-h-5 text-center text-sm text-slate-600" aria-live="polite">
        {grabando
          ? `${leyendaPtt(sesion)} ${segundos}/${TOPE_GRABACION_MS / 1000} s`
          : leyendaPtt(sesion)}
      </p>
    </div>
  );
}

function useSegundosGrabando(grabando: boolean): number {
  const [segundos, setSegundos] = useState(0);
  useEffect(() => {
    if (!grabando) return;
    setSegundos(0);
    const inicio = Date.now();
    const id = setInterval(() => setSegundos(Math.floor((Date.now() - inicio) / 1000)), 250);
    return () => clearInterval(id);
  }, [grabando]);
  return segundos;
}
